import { useState } from 'react';
import { AlertTriangle } from 'lucide-react';

export interface ConfirmOptions {
  /** 对话框标题 */
  title: string;
  /** 补充说明文案 */
  message?: string;
  /** 危险态样式（确认按钮红色） */
  danger?: boolean;
  /** 高危操作要求输入的文本（如名称），输入匹配后才允许确认 */
  requireText?: string;
  /** 确认按钮文案，默认「确认」 */
  confirmText?: string;
  /** 取消按钮文案，默认「取消」 */
  cancelText?: string;
}

interface ConfirmDialogProps extends ConfirmOptions {
  onConfirm: () => void;
  onCancel: () => void;
}

/** 通用确认对话框：风格对齐 Toast 设计令牌，支持危险态与输入确认模式。 */
export function ConfirmDialog({
  title,
  message,
  danger = false,
  requireText,
  confirmText = '确认',
  cancelText = '取消',
  onConfirm,
  onCancel,
}: ConfirmDialogProps) {
  const [input, setInput] = useState('');
  const blocked = Boolean(requireText && input.trim() !== requireText);

  const accentColor = danger ? 'var(--color-error)' : 'var(--color-primary)';

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={title}
      onClick={onCancel}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 10000,
        background: 'rgba(0,0,0,0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 16,
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: 'var(--bg-secondary)',
          border: '1px solid var(--bg-tertiary)',
          borderRadius: 'var(--radius-lg)',
          padding: 20,
          width: 'min(420px, 100%)',
          boxShadow: '0 8px 24px rgba(0,0,0,0.4)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
          {danger && <AlertTriangle size={18} color="var(--color-error)" />}
          <h3 style={{ margin: 0, fontSize: 16, fontWeight: 600, color: 'var(--text-primary)' }}>{title}</h3>
        </div>
        {message && (
          <p style={{ margin: '0 0 12px', fontSize: 13, color: 'var(--text-secondary)', wordBreak: 'break-word' }}>{message}</p>
        )}
        {requireText && (
          <div style={{ marginBottom: 12 }}>
            <label style={{ display: 'block', fontSize: 12, color: 'var(--text-muted)', marginBottom: 4 }}>
              请输入 <code style={{ color: 'var(--text-primary)' }}>{requireText}</code> 以确认操作：
            </label>
            <input
              type="text"
              value={input}
              onChange={e => setInput(e.target.value)}
              style={{
                width: '100%',
                boxSizing: 'border-box',
                padding: '8px 10px',
                fontSize: 13,
                color: 'var(--text-primary)',
                background: 'var(--bg-primary)',
                border: `1px solid ${blocked ? 'var(--color-error)' : 'var(--bg-tertiary)'}`,
                borderRadius: 'var(--radius-lg)',
                outline: 'none',
              }}
            />
          </div>
        )}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button
            type="button"
            onClick={onCancel}
            style={{
              padding: '7px 16px',
              fontSize: 13,
              cursor: 'pointer',
              color: 'var(--text-secondary)',
              background: 'var(--bg-tertiary)',
              border: 'none',
              borderRadius: 'var(--radius-lg)',
            }}
          >
            {cancelText}
          </button>
          <button
            type="button"
            disabled={blocked}
            onClick={onConfirm}
            style={{
              padding: '7px 16px',
              fontSize: 13,
              cursor: blocked ? 'not-allowed' : 'pointer',
              opacity: blocked ? 0.5 : 1,
              color: danger ? '#fff' : 'var(--text-on-success)',
              background: danger ? 'var(--color-error)' : accentColor,
              border: 'none',
              borderRadius: 'var(--radius-lg)',
              fontWeight: 600,
            }}
          >
            {confirmText}
          </button>
        </div>
      </div>
    </div>
  );
}
